import { useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Volume2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { memberAvatarUrl } from '@/lib/avatar'
import { memberName } from '@/lib/guildUi'
import { P, channelPermissions, has } from '@/lib/permissions'
import { cx } from '@/lib/cx'
import type { Channel, Member, VoiceState } from '@/types/api'
import { Avatar } from './Avatar'
import styles from './GuildHoverCard.module.css'

/** Где стоит значок, у которого всплыла карточка (координаты окна). */
export interface HoverAnchor {
  top: number
  bottom: number
  right: number
}

export const RAIL_TOOLTIP_ID = 'rail-tooltip'

/** Кружков в строке голоса; кто не поместился — в последнем кружке «+N». */
const SLOTS = 5

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/**
 * Всплывающая карточка справа от значка в колонке серверов. Мышь она не ловит: иначе, вылезая
 * поверх соседних значков, мешала бы наводить на них и перетаскивать серверы. Рисуется в body —
 * колонка прокручивается и обрезала бы всё, что торчит за её край.
 */
export function RailTooltip({ anchor, label = false, children }: { anchor: HoverAnchor; label?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)

  // высоту знаем только после отрисовки: карточку центрируем по значку и не пускаем за край окна,
  // уголок при этом смотрит на значок. Ставим прямо в стиль до показа кадра — без второй отрисовки
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const place = () => {
      const center = (anchor.top + anchor.bottom) / 2
      const height = el.offsetHeight
      const top = Math.max(8, Math.min(center - height / 2, window.innerHeight - height - 8))
      el.style.top = `${top}px`
      el.style.setProperty('--arrow-y', `${Math.max(12, Math.min(center - top, height - 12))}px`)
    }
    place()
    // пока карточка открыта, в голос заходят и выходят: высота меняется — ставим заново
    const observer = new ResizeObserver(place)
    observer.observe(el)
    return () => observer.disconnect()
  }, [anchor])

  return createPortal(
    // подпись кнопки повторяет её aria-label — читалке второй раз ни к чему
    <div ref={ref} id={RAIL_TOOLTIP_ID} role="tooltip" aria-hidden={label || undefined} className={styles.card} style={{ left: anchor.right + 4 }}>
      {children}
    </div>,
    document.body,
  )
}

interface VoiceGroup {
  channel: Channel
  states: VoiceState[]
}

/** Кто в каких голосовых каналах; каналы — в порядке списка слева: сначала без категории, потом по категориям. */
function voiceGroups(channels: Record<string, Channel> | undefined, states: Record<string, VoiceState> | undefined, visible: (ch: Channel) => boolean): VoiceGroup[] {
  const byChannel = new Map<string, VoiceState[]>()
  for (const vs of Object.values(states ?? {})) {
    if (vs.channel_id) byChannel.set(vs.channel_id, [...(byChannel.get(vs.channel_id) ?? []), vs])
  }
  const groups: VoiceGroup[] = []
  for (const [id, list] of byChannel) {
    const channel = channels?.[id]
    if (channel && visible(channel)) groups.push({ channel, states: list })
  }
  const rank = (ch: Channel) => {
    const category = ch.parent_id ? channels?.[ch.parent_id] : undefined
    return [category ? 1 : 0, category?.position ?? 0, ch.position ?? 0] as const
  }
  return groups.sort((a, b) => {
    const x = rank(a.channel)
    const y = rank(b.channel)
    return x[0] - y[0] || x[1] - y[1] || x[2] - y[2] || (a.channel.id < b.channel.id ? -1 : 1)
  })
}

/** Содержимое карточки сервера: название и кто сейчас в голосе — по строке на канал. */
export function GuildHoverContent({ guildId }: { guildId: string }) {
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channels = useGuildStore((s) => s.channels[guildId])
  const states = useGuildStore((s) => s.voiceStates[guildId])
  const members = useGuildStore((s) => s.members[guildId])
  const meId = useAuthStore((s) => s.user?.id)
  const self = meId ? members?.[meId] : undefined

  // скрытые от человека каналы не называем и тех, кто в них, не показываем — как в списке каналов
  const groups = useMemo(() => {
    if (!guild || !meId) return []
    const viewer = self ?? { user: { id: meId }, roles: [] }
    return voiceGroups(channels, states, (ch) => has(channelPermissions(guild, viewer, ch), P.VIEW_CHANNEL))
  }, [guild, channels, states, self, meId])

  // на большом сервере список участников грузится не целиком: кого в голосе ещё не знаем, спрашиваем
  // по одному, иначе в кружках и подписи были бы id вместо имён (как в списке каналов, правка №22)
  const ensureMembers = useGuildStore((s) => s.ensureMembers)
  const unknown = useMemo(
    () => groups.flatMap((g) => g.states).filter((vs) => !members?.[vs.user_id] && !vs.member).map((vs) => vs.user_id).sort().join(','),
    [groups, members],
  )
  useEffect(() => {
    if (unknown) void ensureMembers(guildId, unknown.split(','))
  }, [guildId, unknown, ensureMembers])

  if (!guild) return null
  return (
    <>
      {/* значок не повторяем — карточка и так стоит у него. Название уже есть в подписи кнопки */}
      <div className={styles.name} aria-hidden="true">{guild.name}</div>
      {groups.length ? (
        // строки — сеткой: кружки разных каналов встают в один столбец, а не вразнобой за названиями
        <div className={cx(styles.voices, groups.length > 1 && styles.named)}>
          {groups.map((g) => (
            <VoiceRow key={g.channel.id} guildId={guildId} group={g} members={members} named={groups.length > 1} />
          ))}
        </div>
      ) : null}
    </>
  )
}

/** Строка голоса: динамик, название канала (если каналов несколько) и кружки людей. */
function VoiceRow({ guildId, group, members, named }: { guildId: string; group: VoiceGroup; members: Record<string, Member> | undefined; named: boolean }) {
  const shown = group.states.length > SLOTS ? group.states.slice(0, SLOTS - 1) : group.states
  const rest = group.states.length - shown.length
  const people = shown.map((vs) => {
    const member = members?.[vs.user_id] ?? vs.member
    // пока имя не пришло — пустой кружок, а не первая цифра id
    return { id: vs.user_id, name: member ? memberName(member) : '', avatar: memberAvatarUrl(guildId, member) }
  })
  const names = `${people.map((p) => p.name || 'участник').join(', ')}${rest ? ` и ещё ${rest}` : ''}`
  return (
    <div className={styles.voice}>
      <Volume2 size={16} aria-hidden="true" />
      {named ? <span className={styles.channel} aria-hidden="true">{group.channel.name}</span> : null}
      <span className={styles.people} aria-hidden="true">
        {people.map((p) => (
          <Avatar key={p.id} className={styles.face} size={24} initial={p.name.slice(0, 1).toUpperCase()} color={colorOf(p.id)} src={p.avatar} />
        ))}
        {rest ? <span className={styles.more}>+{rest}</span> : null}
      </span>
      <span className="sr-only">{named ? `В голосе «${group.channel.name ?? ''}»: ${names}` : `В голосе: ${names}`}</span>
    </div>
  )
}
